import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'
import { observer } from 'mobx-react'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { IArticleBase, ICompanyBase, IJobBase, Language } from '@/types'
import { CompanyProfileAboutUs } from '@/components/organisms/CompanyProfileAboutUs'
import { CompanyProfileService } from '@/components/organisms/CompanyProfileService'
import { CompanyProfileArticle } from '@/components/organisms/CompanyProfileArticle'
import { CompanyProfileMember } from '@/components/organisms/CompanyProfileMember'
import { CompanyProfileFinance } from '@/components/organisms/CompanyProfileFinance'
import { CompanyProfileInvestmentComment } from '@/components/organisms/CompanyProfileInvestmentComment'
import { getCompanyLogo, getStaticStores, sanitizeUrl, useStores } from '@/utils'
import { AlertBanner } from '@/components/molecules/AlertBanner'
import { ProfilePortfolio } from '@/components/organisms/ProfilePortfolio'
import { MyHead } from '@/components/molecules/MyHead'
import { ProfileInvestmentPolicy } from '@/components/organisms/ProfileInvestmentPolicy'
import { CompanyDetailHeader } from '@/components/organisms/CompanyDetailHeader'
import { CompanyProfileJobs } from '@/components/organisms/CompanyProfileJobs'
import { CompanyProfileInvestorComment } from '@/components/organisms/CompanyProfileInvestorComment'
import { ProfileCompletionPromotionPopup } from '@/components/organisms/ProfileCompletionPromotionPopup'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { CompanyProfileOverview } from '@/components/organisms/CompanyProfileOverview'
import { SetUpProfileToSendDMsPromotionModal } from '@/components/molecules/SetUpProfileToSendDMsPromotionModal'
import { CompanyProfileReferences } from '@/components/organisms/CompanyProfileReferences'
import styles from './index.module.scss'

type Props = {
  company: ICompanyBase
  articles: IArticleBase[]
  companyJobs: IJobBase[]
}

const Company: NextPage<Props> = observer(({ company, articles, companyJobs }) => {
  const { t } = useTranslation(['companies-[id]', 'companies-users-[id]'])
  const { companies, viewer } = useStores()
  const companyInstance = companies.createCompanyInstance(company)
  const title = t('companies-users-[id]:のプロフィール', { name: `${company.name}` })
  let description = ''
  if (company?.aboutUs) {
    const descriptionPrefix = company?.aboutUs.substring(0, 130)
    const descriptionSuffix = company?.aboutUs.length > 130 ? '...' : ''
    description = `${descriptionPrefix}${descriptionSuffix}`
  }

  useEffect(() => {
    if (!company || !companyInstance) {
      return
    }

    if (viewer.isInitialized && viewer.isSignedIn) {
      companyInstance.fetch()
    }
  }, [viewer.isInitialized, company])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title={title} description={description} image={getCompanyLogo(company, true)} twitterCardType='summary' />
      <ProfileSingleColumnContentTemplate header={<CompanyDetailHeader company={companyInstance} />} hasHeaderImage>
        {!company?.companyMembers.length && (
          <AlertBanner location='pageTop' styleType='warning'>
            <span>
              {t('この会社ページにはまだ編集者がいません。自分の会社の場合は編集権限を')}
              <a href={sanitizeUrl(process.env.NEXT_PUBLIC_CLAIM_COMPANY_FORM_URL)} target='_blank' rel='noreferrer'>
                {t('こちら')}
              </a>
              {t('からリクエストできます。')}
            </span>
          </AlertBanner>
        )}
        <div className={styles.aboutUs}>
          <CompanyProfileAboutUs company={company} />
        </div>
        <div className={styles.overview}>
          <CompanyProfileOverview company={company} />
        </div>
        <div className={styles.jobs}>
          <CompanyProfileJobs jobs={companyJobs} companySlug={company.slug} />
        </div>
        <div className={styles.products}>
          <CompanyProfileService company={company} />
        </div>
        <div className={styles.articles}>
          <CompanyProfileArticle articles={articles} companySlug={company.slug} />
        </div>
        <div className={styles.receivedInvestment}>
          <CompanyProfileFinance company={company} />
        </div>
        <div className={styles.investmentComment}>
          <CompanyProfileInvestorComment company={company} />
        </div>
        {/* 投資先が登録されている企業のみ表示 */}
        {company?.investments.length > 0 && (
          <>
            <div className={styles.portfolio}>
              <ProfilePortfolio investments={company?.investments} />
            </div>
            <div className={styles.investmentComment}>
              <CompanyProfileInvestmentComment company={company} />
            </div>
          </>
        )}
        {company?.companyReferences?.nodes?.length > 0 && (
          <div className={styles.references}>
            <CompanyProfileReferences company={company} />
          </div>
        )}
        <div className={styles.members}>
          <CompanyProfileMember company={company} />
        </div>
        {/* 投資目的系プロフィールは VC のみ表示 */}
        {company?.isVc && (
          <div className={styles.investmentPolicy}>
            <ProfileInvestmentPolicy
              investmentTargetMarkets={company.investmentTargetMarkets}
              investmentTargetRounds={company.investmentTargetRounds}
              minInvestmentAmount={Number(company.minInvestmentAmount)}
              maxInvestmentAmount={Number(company.maxInvestmentAmount)}
              currencyUnit={company.currencyUnit}
            />
          </div>
        )}
        {company?.isVc ? (
          <>
            <ProfileCompletionPromotionPopup type='startup' />
          </>
        ) : (
          <>
            <ProfileCompletionPromotionPopup type='investor' />
          </>
        )}
        <SetUpProfileToSendDMsPromotionModal viewer={viewer.viewer} />
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const { companies, jobs, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const companySlug = params.id as string
  const [company, articles, companyJobs] = await Promise.all([
    // TODO: limitOfJob の指定が必要か確認する
    // hide があった場合、少なく表示されるのでバッファを持たせるために 5 を指定
    // これを解決するためにはサーバー側の調整が必要。CompanyType に新しく show_company_reference みたいな show な reference のみを返すフィールドが必要
    companies.fetchCompany({ slug: companySlug, limitOfJob: 100, limitOfReference: 5 }),
    companies.fetchCompanyArticles({ slug: companySlug, limit: 3, shouldRefresh: true }),
    jobs.fetchCompanyJobs({ companySlug, limit: 3, shouldRefresh: true }),
  ])

  if (!company) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      company,
      articles,
      companyJobs,
      ...(await serverSideTranslations(locale, [
        'global',
        'companies-users-[id]',
        'companies-[id]',
        'jobs',
        'angels-vcs-startups',
        'p',
      ])),
    },
  }
}

export default Company
